import { useFormik } from 'formik';
import { number, object, string } from 'yup';
import { i18n } from '~/lib/i18n';
import { useLayout } from '../../context';
import type { LayoutList__Layout as Layout } from '../../queries/list.generated';
import type { ZoneFormValues } from '../components';

const sizeField = (max: number, translationKey: string) => {
  return number()
    .required()
    .label(i18n.t(translationKey))
    .moreThan(0)
    .max(
      max,
      i18n.t('pages.layouts.zone_form.validation.size_max', {
        field: i18n.t(translationKey).toLowerCase(),
        max,
      }),
    )
    .transform((currentValue) => {
      return isNaN(parseFloat(currentValue)) ? undefined : parseFloat(currentValue);
    });
};

const heightField = (layout: Layout) => {
  return sizeField(layout.height, 'common.labels.height');
};

const widthField = (layout: Layout) => {
  return sizeField(layout.width, 'common.labels.width');
};

const leftField = (layout: Layout) => {
  return number()
    .required()
    .label(i18n.t('pages.layouts.zone_form.labels.left_x'))
    .min(0)
    .when('$width', ([width], schema) => {
      const maxLeft = layout.width - width;
      return schema.max(
        maxLeft,
        i18n.t('pages.layouts.zone_form.validation.left_max', {
          maxLeft,
          layoutWidth: layout.width,
          zoneWidth: width,
        }),
      );
    })
    .transform((currentValue) => {
      return isNaN(parseFloat(currentValue)) ? undefined : parseFloat(currentValue);
    });
};

const topField = (layout: Layout) => {
  return number()
    .required()
    .label(i18n.t('pages.layouts.zone_form.labels.top_y'))
    .min(0)
    .when('$height', ([height], schema) => {
      const maxTop = layout.height - height;
      return schema.max(
        maxTop,
        i18n.t('pages.layouts.zone_form.validation.top_max', {
          maxTop,
          layoutHeight: layout.height,
          zoneHeight: height,
        }),
      );
    })
    .transform((currentValue) => {
      return isNaN(parseFloat(currentValue)) ? undefined : parseFloat(currentValue);
    });
};

export const useZoneFormik = (
  initialValues: ZoneFormValues,
  onSubmit: (values: ZoneFormValues) => Promise<void>,
) => {
  const layout = useLayout();

  const validationSchema = object({
    height: heightField(layout),
    left: leftField(layout),
    name: string().trim().required(i18n.t('pages.layouts.zone_form.validation.name_required')),
    top: topField(layout),
    width: widthField(layout),
  });

  const handleSubmit = async (values: ZoneFormValues) => {
    const variables = validationSchema.cast(values);
    await onSubmit(variables);
  };

  return useFormik({
    enableReinitialize: true,
    initialValues: initialValues,
    onSubmit: handleSubmit,
    validateOnBlur: true,
    validateOnMount: true,
    validationSchema: validationSchema,
  });
};
